import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function Field({ label, id, ...props }) {
  return (
    <div>
      <label htmlFor={id} className="font-mono-label text-[11px] tracking-label uppercase text-white/50 block mb-2">{label}</label>
      <input
        id={id}
        data-testid={`field-${id}`}
        className="w-full bg-transparent border-b border-white/20 focus:border-accent text-white placeholder:text-white/25 py-3 outline-none transition-colors"
        {...props}
      />
    </div>
  );
}

export function TextAreaField({ label, id, ...props }) {
  return (
    <div>
      <label htmlFor={id} className="font-mono-label text-[11px] tracking-label uppercase text-white/50 block mb-2">{label}</label>
      <textarea
        id={id}
        data-testid={`field-${id}`}
        rows={4}
        className="w-full bg-transparent border-b border-white/20 focus:border-accent text-white placeholder:text-white/25 py-3 outline-none transition-colors resize-none"
        {...props}
      />
    </div>
  );
}

export function SelectField({ label, id, value, onChange, options, placeholder, colorful = false, palette = [] }) {
  // Normalise options into { value, color } shape when a palette is provided
  const items = options.map((o, i) => {
    if (typeof o === "string") {
      return { value: o, color: palette[i % (palette.length || 1)] || "#F0A831" };
    }
    return { value: o.value, color: o.color || palette[i % (palette.length || 1)] || "#F0A831" };
  });

  if (!colorful) {
    return (
      <div>
        <label className="font-mono-label text-[11px] tracking-label uppercase opacity-60 block mb-2">{label}</label>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger
            data-testid={`field-${id}`}
            className="w-full bg-transparent border-0 border-b border-current/30 rounded-none py-3 h-auto px-0 focus:ring-0 focus:border-accent data-[placeholder]:opacity-50"
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent
            className="border-white/10 rounded-md"
            style={{ backgroundColor: "#0F172A", color: "#ffffff" }}
          >
            {items.map((o) => (
              <SelectItem
                key={o.value}
                value={o.value}
                className="cursor-pointer focus:!bg-accent focus:!text-[#050B14]"
                style={{ color: "#ffffff" }}
              >
                <span style={{ color: "inherit" }}>{o.value}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // Colorful variant — pill trigger + colored dots per option
  const activeColor = items.find((o) => o.value === value)?.color;
  return (
    <div>
      <label className="font-mono-label text-[11px] tracking-label uppercase text-accent/90 block mb-2">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          data-testid={`field-${id}`}
          className="relative w-full h-auto pl-12 pr-4 py-4 rounded-2xl border border-white/15 bg-gradient-to-br from-[#0B1B30] to-[#050B14] font-medium hover:border-accent/60 focus:ring-0 focus:border-accent transition-colors shadow-lg shadow-black/20 data-[placeholder]:opacity-70"
          style={{
            color: "#ffffff",
            ...(activeColor ? { boxShadow: `0 0 0 1px ${activeColor}55 inset, 0 10px 25px -10px ${activeColor}55` } : {}),
          }}
        >
          <span
            className="absolute left-5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full pointer-events-none"
            style={{
              backgroundColor: activeColor || "#ffffff33",
              boxShadow: activeColor ? `0 0 12px ${activeColor}` : undefined,
            }}
            aria-hidden
          />
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent
          className="border border-white/10 rounded-xl shadow-2xl shadow-black/40 overflow-hidden min-w-[--radix-select-trigger-width]"
          style={{ backgroundColor: "#0B1B30", color: "#ffffff" }}
        >
          {items.map((o) => (
            <SelectItem
              key={o.value}
              value={o.value}
              className="cursor-pointer focus:!bg-white/[0.08] data-[state=checked]:!bg-white/[0.05] pl-8 pr-4 py-2.5 relative"
              style={{ color: "#ffffff" }}
            >
              <span
                className="absolute left-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full pointer-events-none"
                style={{ backgroundColor: o.color, boxShadow: `0 0 10px ${o.color}` }}
                aria-hidden
              />
              <span style={{ color: "#ffffff" }}>{o.value}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Honeypot input (hidden from users, catches bots)
export function Honeypot({ value, onChange }) {
  return (
    <input
      type="text"
      name="company"
      tabIndex={-1}
      autoComplete="off"
      value={value}
      onChange={onChange}
      className="hidden"
      aria-hidden="true"
    />
  );
}

export function SuccessScreen({ title, body, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="border border-accent/40 bg-accent/5 p-10 md:p-16 text-center"
      data-testid={testid}
    >
      <CheckCircle2 className="w-14 h-14 text-accent mx-auto" />
      <h3 className="font-display text-3xl md:text-4xl text-white mt-6 tracking-tight">{title}</h3>
      <p className="text-white/60 mt-4 max-w-md mx-auto leading-relaxed">{body}</p>
      <Link to="/" className="inline-block mt-8 font-mono-label text-[11px] tracking-label uppercase text-accent link-underline">← Back to home</Link>
    </motion.div>
  );
}

export function SubmitButton({ loading, children, testid }) {
  return (
    <button
      type="submit"
      disabled={loading}
      data-testid={testid}
      className="group inline-flex items-center gap-3 bg-accent text-[#050B14] px-10 py-4 font-mono-label text-[11px] tracking-label uppercase font-medium hover:bg-white transition-colors disabled:opacity-60"
    >
      {loading ? "Sending…" : children}
    </button>
  );
}
